import React, { useState, useEffect, useCallback } from "react";
import { authHeaders } from "../lib/auth";
import HydrationImpact from "./HydrationImpact";
import AppHeader from "./AppHeader";
import RetentionInsightCard from "./RetentionInsightCard";

/* ============================================================
   GENEROSITY(TM) WTR APP - MY HYDRATION (LIVE, OZ-PRIMARY)
   ------------------------------------------------------------
   - US market: ounces are the primary unit, with a small liter
     reference under/beside each hero number. Quick-log chips are
     ounce-native vessel sizes (8 / 12 / 16 oz).
   - The NHS formula and storage stay in LITERS on the server.
     Unit conversion is done server-side so tiles and card strings
     can never disagree. This client renders { val, unit, ref }.
   - Calls the Sales Engine directly with the service token, same
     pattern as the rest of the app (CORS allowlisted).

   RETENTION INSIGHT CONSOLIDATION (this revision):
   - The IntakeBar is now clean: number, goal, bar, verified and
     manual split, stale-sync warning. The tier chip, both info
     dots, the estimated range clause, the gray insight strip,
     and the epiphany trigger all moved into RetentionInsightCard,
     the collapsible Hydration Efficiency card (RetentionInsightCard.jsx,
     renamed in the v8 surgical pass) mounted above the target card
     the target breakdown.
   - HONESTY GATE: RetentionInsightCard renders ONLY when the
     server sends data.retention, or in labeled demo mode. Its
     internal mock payload must never reach a live user (Commit 5
     rule: live mode never renders fabricated data). Ethan: the
     retention payload contract is documented at the top of
     RetentionInsightCard.jsx; compute it in the daily rollup
     pipeline plus on intake events, never per render.
   - The Hydration Epiphany modal now lives at page level and
     opens from the link inside the Hydration Efficiency card.
   ============================================================ */

const API_BASE = "https://generosity-sales-engine-mvp-api.onrender.com";
// Phase 2: identity rides via authHeaders() (X-WTR-Auth session token).
const UNITS = "oz"; // primary unit for the US market

const MOCK_PAYLOAD = {
  units: "oz",
  user: { firstName: "Micah" },
  nhs: { score: 30, status: "Behind Goal", projectedEveningScore: 62, projectionNote: "Drink 20 oz this hour to reach 62 tonight." },
  intake: {
    target: { val: 176, unit: "oz", ref: "5.2 L" },
    absorbed: { val: 44, unit: "oz", ref: "1.3 L" },
    verified: { val: 0, unit: "oz", ref: "0.0 L" },
    logged: { val: 51, unit: "oz", ref: "1.5 L" },
    pct: 25,
    bottleSyncAgeHours: 16,
  },
  targetBreakdown: [
    { label: "Baseline for your body metrics", primary: "+122 oz", sign: "base" },
    { label: "Activity level: active", primary: "+37 oz", sign: "+" },
    { label: "Climate: hot", primary: "+20 oz", sign: "+" },
  ],
  targetTotalRef: "5.2 L",
  intelCards: [
    { id: "demo-card", tier: "hypothesis_only", eyebrow: "WTR INTEL",
      title: "Demo data is showing",
      body: "Save your profile with a valid email, or check the API connection, to see your live Hydration Score.",
      action: "Open Profile" },
  ],
};

const BRAND = {
  blue: "#51B0E6", gray: "#A6A8AB", card: "#F0F1F3", ink: "#2B2F33",
  amber: "#C8923A", red: "#B84A4A", orange: "#C8803C",
  green: "#4A8A6F", attention: "#C89B3C",
};

const TIER_STYLES = {
  verified: { fg: "#3D7A5C", label: "verified" },
  corroborated: { fg: "#3D7AA6", label: "corroborated" },
  developing: { fg: "#A6792E", label: "developing" },
  hypothesis_only: { fg: "#7A7D80", label: "hypothesis only" },
};

function scoreColor(score) {
  if (score >= 90) return BRAND.green;
  if (score >= 70) return BRAND.blue;
  if (score >= 50) return BRAND.attention;
  if (score >= 30) return BRAND.orange;
  return BRAND.red;
}

function getProfileEmail() {
  try {
    const p = JSON.parse(localStorage.getItem("wtr_profile") || "{}");
    const email = (p.email || "").trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
  } catch (e) { return null; }
}

// Demo mode is EXPLICIT ONLY: append ?demo=1 to the URL for investor demos.
// It is never an error fallback (Commit 5). The old behavior rendered
// fabricated scores with a confidence pill on any fetch failure, including
// the deliberate WTR+ 403, which swallowed the paywall and violated the
// confidence-tier honesty rule. A real failure now gets an honest offline
// state; a tier refusal routes to the locked (upgrade) state.
const DEMO_FLAG = (() => {
  try { return new URLSearchParams(window.location.search).get("demo") === "1"; }
  catch (e) { return false; }
})();

function useHydrationSummary() {
  const [data, setData] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [demoReason, setDemoReason] = useState("");
  const [locked, setLocked] = useState(false);   // 403 WTR_PLUS_REQUIRED
  const [offline, setOffline] = useState(false); // genuine fetch failure
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const email = getProfileEmail();
    if (!email) {
      setData(MOCK_PAYLOAD); setDemoMode(true);
      setDemoReason("SAVE PROFILE TO GO LIVE"); setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        API_BASE + "/api/wtr/hydration/summary?units=" + UNITS + "&email=" + encodeURIComponent(email),
        { headers: authHeaders({ Accept: "application/json" }) }
      );
      if (res.status === 403) {
        // The server refused the TIER, not the network. Route to the
        // locked state so the upgrade ask renders instead of fake data.
        let code = "";
        try { const j = await res.json(); code = j && j.code; } catch (e2) {}
        if (code === "WTR_PLUS_REQUIRED") {
          setData(null); setDemoMode(false); setDemoReason("");
          setLocked(true); setOffline(false);
          return;
        }
        throw new Error("summary 403");
      }
      if (!res.ok) throw new Error("summary " + res.status);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "summary failed");
      setData(json); setDemoMode(false); setDemoReason("");
      setLocked(false); setOffline(false);
    } catch (e) {
      if (DEMO_FLAG) {
        setData(MOCK_PAYLOAD); setDemoMode(true);
        setDemoReason("DEMO DATA - EXPLICIT DEMO MODE");
      } else {
        setData(null); setDemoMode(false); setDemoReason("");
        setOffline(true); setLocked(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refresh]);

  return { data, demoMode, demoReason, loading, refresh, locked, offline };
}

function NhsArc({ score, projected, status }) {
  const size = 240, stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const arcSpan = 0.75;
  const offset = (v) => c * (1 - (arcSpan * Math.min(v, 100)) / 100);
  const color = scoreColor(score);
  return (
    <div style={{ position: "relative", width: size, height: size, margin: "0 auto" }}>
      <svg width={size} height={size} style={{ transform: "rotate(135deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={BRAND.card} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={c * (1 - arcSpan)} strokeLinecap="round" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={BRAND.blue} strokeWidth={stroke} opacity="0.25"
          strokeDasharray={c} strokeDashoffset={offset(projected)} strokeLinecap="round" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset(score)} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 800ms ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", textAlign: "center" }}>
        <div className="gw-eyebrow" style={{ color: BRAND.gray }}>HYDRATION SCORE</div>
        <div className="gw-score" style={{ color }}>{score}</div>
        <div className="gw-status" style={{ color }}>{status}</div>
        {projected > score && (
          <div className="gw-micro" style={{ color: BRAND.blue, marginTop: 4 }}>projected {projected} tonight</div>
        )}
      </div>
    </div>
  );
}

function TierChip({ tier }) {
  // Confidence tier chip, redesigned per tier spec section 6 (Commit 7):
  // slim, Barlow Condensed 10px caps, 1px border in the tier color,
  // transparent fill, no shadow. Reads as provenance metadata (like a
  // camera's EXIF), never as a second headline.
  // v8 SURGICAL PASS (Jul 14 2026, founder-directed): developing and
  // hypothesis_only chips no longer render anywhere on this screen.
  // Tier data still flows in payloads; conservative copy and the
  // estimate disclosures carry the honesty. verified / corroborated
  // chips remain as trust signals.
  if (tier !== "verified" && tier !== "corroborated") return null;
  const t = TIER_STYLES[tier] || TIER_STYLES.hypothesis_only;
  return (
    <span style={{ background: "transparent", color: t.fg, border: "1px solid " + t.fg,
      borderRadius: 999, padding: "1.5px 8px", fontSize: 10, fontWeight: 600,
      letterSpacing: "0.08em", textTransform: "uppercase",
      fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif", whiteSpace: "nowrap" }}>
      {t.label}
    </span>
  );
}

// Lightbulb for the epiphany header.
function LightbulbIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.6.45.9 1.05.9 1.7V16h5.4v-.5c0-.65.3-1.25.9-1.7A6 6 0 0 0 12 3Z"
        stroke={BRAND.blue} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

// The Hydration Epiphany modal. No em or en dashes (brand rule). No navy.
// Opens from the link inside the Hydration Efficiency card.
function HydrationEpiphany({ onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="The Hydration Epiphany"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(20,24,28,0.45)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF", width: "100%", maxWidth: 520,
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          padding: "26px 20px 32px", maxHeight: "88vh", overflowY: "auto",
          color: BRAND.ink, fontFamily: "'Montserrat', sans-serif",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
          <LightbulbIcon />
        </div>
        <h2 style={{
          fontFamily: "'Barlow Condensed', sans-serif", fontSize: 24, fontWeight: 700,
          textAlign: "center", margin: "0 0 16px",
        }}>
          The Hydration Epiphany
        </h2>

        <p style={{ fontSize: 14.5, lineHeight: 1.6, margin: "0 0 12px" }}>
          Most apps count what you drink. Your body only benefits from what it keeps.
        </p>
        <p style={{ fontSize: 14.5, lineHeight: 1.6, margin: "0 0 12px" }}>
          When you drink, nearly all of that water crosses into your bloodstream. But
          absorbing it isn{"\u2019"}t the same as keeping it. Over the next few hours, your body
          makes a second decision: how much of that fluid to hold onto, and how much to
          send out.
        </p>
        <p style={{ fontSize: 14.5, lineHeight: 1.6, margin: "0 0 6px" }}>
          That decision isn{"\u2019"}t random. Water carrying bioavailable minerals like sodium,
          potassium, and magnesium signals your body to hold fluid and pull it into your
          cells. Mineral poor or contaminated water passes through faster, and more of it
          leaves as urine while your kidneys and liver clear what doesn{"\u2019"}t belong.
        </p>
        <div style={{ marginBottom: 14 }}><TierChip tier="corroborated" /></div>

        <p style={{ fontSize: 14.5, lineHeight: 1.6, margin: "0 0 6px" }}>
          So two people can drink the same amount and end up hydrated very differently.
          Your goal is measured in what your body actually retains: your verified intake,
          adjusted for your water{"\u2019"}s quality, how fast you drank it, and your environment.
          Our models do that math for you, mirroring how your body decides what to keep and
          what to let go.
        </p>
        <div style={{ marginBottom: 16 }}><TierChip tier="developing" /></div>

        <div style={{ background: BRAND.card, borderRadius: 12, padding: "12px 14px", margin: "0 0 16px" }}>
          <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 14 }}>Absorption, not just intake.</p>
          <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 14 }}>Absorption efficiency model, not a concentration adjustment model.</p>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Adaptive range, not a fixed threshold.</p>
        </div>

        <p style={{ fontSize: 13.5, lineHeight: 1.6, margin: "0 0 6px", color: "#4A4E53" }}>
          Losing just 1.5% of your body{"\u2019"}s water measurably impairs physical and cognitive
          performance. That{"\u2019"}s why what you retain matters more than what you pour.
        </p>
        <div style={{ marginBottom: 20 }}><TierChip tier="corroborated" /></div>

        <button
          onClick={onClose}
          style={{
            width: "100%", padding: "12px 0", background: BRAND.blue, color: "#FFFFFF",
            border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700,
            fontFamily: "'Montserrat', sans-serif", cursor: "pointer",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

/* IntakeBar, three-second pass (Jul 15 2026, founder-directed clarity
   sweep). The card must answer "where am I today" in one glance: number,
   goal, bar, and exactly two quiet lines (verified/manual split, sync
   state). Removed relative to the prior revision, with destinations:
   - "Towards your N unit daily goal" long form -> merged into the goal
     subline ("of 66 oz goal").
   - The liters reference line (ret.ref of target.ref) -> cut. Pure unit
     duplication of the oz figures; the refs still ship in the payload.
   - The two-line Verified block + separate manual line -> one split line.
   - The policy sentence ("Only bottle-verified water moves your retained
     total...") -> relocated to the top of the Hydration Efficiency card's
     expanded body (RetentionInsightCard v9), the surface whose job is
     explaining verified vs retained. Relocated, not deleted.
   The stale-sync warning stays (operational and actionable) compressed to
   one line. */
function IntakeBar({ intake }) {
  const stale = intake.bottleSyncAgeHours == null || intake.bottleSyncAgeHours > 1;

  // Retention is the goal currency. Fall back to absorbed for older payloads
  // so the card never crashes on the mock or a stale response.
  const ret = intake.retained || intake.absorbed;
  const pct = intake.target && intake.target.val > 0
    ? Math.min(100, Math.round((ret.val / intake.target.val) * 100))
    : (intake.pct || 0);

  return (
    <div className="gw-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span>
          <span className="gw-bignum">{ret.val}</span>
          <span className="gw-bigunit"> {ret.unit}</span>
          <span className="gw-bigunit"> Retained Hydration</span>
          <span className="gw-dim" style={{ display: "block", marginTop: 2 }}>
            of {intake.target.val} {intake.target.unit} goal
          </span>
        </span>
        <span className="gw-dim">{pct}%</span>
      </div>
      <div style={{ height: 10, borderRadius: 999, background: BRAND.card, marginTop: 10, overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", background: BRAND.blue, borderRadius: 999, transition: "width 600ms ease" }} />
      </div>
      <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 10 }}>
        <span style={{ color: intake.verified.val > 0 ? BRAND.green : BRAND.gray, fontWeight: 700 }}>
          {intake.verified.val} {intake.verified.unit}
        </span>
        {" verified from your bottle "}{"\u00B7"}{" "}
        {intake.logged.val} {intake.logged.unit} manual
      </div>
      {stale && (
        <div className="gw-micro" style={{ color: BRAND.amber, marginTop: 6 }}>
          {intake.bottleSyncAgeHours == null
            ? "Bottle not synced yet \u00B7 Pair on the WTR BTL tab"
            : "Bottle last synced " + intake.bottleSyncAgeHours + "h ago \u00B7 Reconnect on the WTR BTL tab"}
        </div>
      )}
    </div>
  );
}

function TargetBreakdown({ rows, targetTotalRef, targetVal, targetUnit }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="gw-card">
      <button className="gw-rowbtn" onClick={() => setOpen(!open)}>
        <span className="gw-cardtitle">Why your target is {targetVal} {targetUnit} today</span>
        <span style={{ color: BRAND.gray }}>{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0",
              borderTop: i === 0 ? "none" : "1px solid " + BRAND.card }}>
              <span className="gw-body" style={{ textTransform: "capitalize" }}>{r.label}</span>
              <span className="gw-body" style={{ fontWeight: 700, color: r.sign === "+" ? BRAND.blue : BRAND.ink }}>
                {r.primary}
              </span>
            </div>
          ))}
          <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 8 }}>
            Total target {targetVal} {targetUnit} ({targetTotalRef}). Adaptive range, not a fixed
            threshold. Your target moves with your biometrics, activity, and climate.
          </div>
        </div>
      )}
    </div>
  );
}

function IntelCard({ card, onAction }) {
  return (
    <div className="gw-card" style={{ borderLeft: "4px solid " + BRAND.blue }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="gw-eyebrow" style={{ color: BRAND.blue }}>
          {card.eyebrow}<span style={{ verticalAlign: "super", fontSize: 8 }}>TM</span>
        </span>
        <TierChip tier={card.tier} />
      </div>
      <div className="gw-cardtitle" style={{ marginTop: 8 }}>{card.title}</div>
      <p className="gw-body" style={{ marginTop: 6 }}>{card.body}</p>
      <button className="gw-action" onClick={() => onAction(card)}>{card.action}</button>
    </div>
  );
}

/* ============================================================
   INTAKE HISTORY (added 2026-07-07, replaces QuickLog)
   ------------------------------------------------------------
   Logging moved to the My Intake tab, which owns all intake
   entry. This card is the quick read of recent history: one bar
   per day, bar height is the day's absorbed total, the green
   base segment is the bottle-verified share (same semantics as
   the Daily Hydration card), and a full-saturation bar marks a
   day that met its adaptive target. Days with no data render as
   hairline gaps, never as zeros: an unopened app is unknown, not
   dehydrated.
   Data: GET /hydration/history returns snapshotByDay (from the
   wtr_daily_snapshot rollup, absorbed/verified/target per local
   date) plus intakeByDay (verified from bottle events). The two
   are merged per day; verified from events wins because it is
   authoritative even on days without a snapshot row.
   ============================================================ */

const HISTORY_DAYS = 30;

function localDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + dd;
}

function buildHistoryDays(n, intakeByDay, snapshotByDay) {
  const ev = {};
  (intakeByDay || []).forEach(r => { ev[r.date] = r; });
  const sn = {};
  (snapshotByDay || []).forEach(r => { sn[r.date] = r; });
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = localDateKey(d);
    const s = sn[key];
    const e = ev[key];
    const verified = e && e.verified != null ? e.verified
      : (s && s.verified != null ? s.verified : 0);
    const absorbed = s && s.absorbed != null ? s.absorbed : null;
    const target = s && s.target != null ? s.target : null;
    // The bar shows the best-known total for the day: absorbed when the
    // snapshot has it, otherwise the verified share alone (bottle synced but
    // the app never opened). Null means genuinely no data: render a gap.
    const total = absorbed != null ? absorbed : (verified > 0 ? verified : null);
    out.push({ key, date: d, verified, absorbed, target, total, hasData: total != null });
  }
  return out;
}

// Deterministic demo series so the card demos well under the DEMO chip.
// Fabrication here is sanctioned product behavior because the whole screen is
// explicitly labeled demo data in this mode; live mode never invents a bar.
function demoHistoryDays(n) {
  const totals = [58, 71, 64, 88, 76, 52, 84, 69, 91, 60, 74, 86, 55, 79];
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const total = totals[(n - 1 - i) % totals.length];
    out.push({
      key: localDateKey(d), date: d,
      total, absorbed: total,
      verified: Math.round(total * 0.45),
      target: 84, hasData: true,
    });
  }
  return out;
}

function IntakeHistoryCard({ demoMode }) {
  const [view, setView] = useState("week"); // "week" | "month"
  const [days, setDays] = useState(null);   // merged per-day series, oldest first
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (demoMode) {
        if (!cancelled) { setDays(demoHistoryDays(HISTORY_DAYS)); setState("ready"); }
        return;
      }
      const email = getProfileEmail();
      if (!email) {
        if (!cancelled) { setDays(demoHistoryDays(HISTORY_DAYS)); setState("ready"); }
        return;
      }
      try {
        const res = await fetch(
          API_BASE + "/api/wtr/hydration/history?units=" + UNITS +
          "&days=" + HISTORY_DAYS + "&email=" + encodeURIComponent(email),
          { headers: authHeaders({ Accept: "application/json" }) }
        );
        if (!res.ok) throw new Error("history " + res.status);
        const json = await res.json();
        if (!json.ok) throw new Error(json.error || "history failed");
        if (!cancelled) {
          setDays(buildHistoryDays(HISTORY_DAYS, json.intakeByDay, json.snapshotByDay));
          setState("ready");
        }
      } catch (e) {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [demoMode]);

  const span = view === "week" ? 7 : HISTORY_DAYS;
  const visible = days ? days.slice(-span) : [];
  const withData = visible.filter(d => d.hasData);
  const withTarget = withData.filter(d => d.absorbed != null && d.target != null);
  const goalHits = withTarget.filter(d => d.absorbed >= d.target).length;
  const avg = withData.length
    ? Math.round(withData.reduce((s, d) => s + d.total, 0) / withData.length)
    : null;
  const scale = Math.max(1, ...visible.map(d => d.total || 0), ...visible.map(d => d.target || 0));
  const BAR_AREA = 84;
  const dayLetter = (d) => ["S", "M", "T", "W", "T", "F", "S"][d.getDay()];
  const monthLabel = (d) => (d.toLocaleDateString(undefined, { month: "short", day: "numeric" }));

  return (
    <div className="gw-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="gw-cardtitle">Intake History</span>
        <div style={{ display: "flex", gap: 6 }}>
          {[["week", "Week"], ["month", "Month"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 11.5, fontWeight: 700,
              borderRadius: 999, padding: "4px 12px", cursor: "pointer",
              border: "1.5px solid " + BRAND.blue,
              background: view === k ? BRAND.blue : "#FFFFFF",
              color: view === k ? "#FFFFFF" : BRAND.blue,
            }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 2 }}>
        Daily totals, bottle-verified share in green. Full bars met that day{"\u2019"}s
        adaptive target. Log drinks on the My Intake tab.
      </div>

      {state === "loading" && (
        <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 16 }}>Loading history{"\u2026"}</div>
      )}
      {state === "error" && (
        <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 16 }}>
          History is unavailable right now. Pull to refresh or check back shortly.
        </div>
      )}

      {state === "ready" && withData.length === 0 && (
        <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 16, lineHeight: 1.6 }}>
          Your history builds from today. Drink from your bottle or log on the
          My Intake tab, and this chart fills in one day at a time.
        </div>
      )}

      {state === "ready" && withData.length > 0 && (
        <>
          <div style={{
            display: "flex", alignItems: "flex-end",
            gap: view === "week" ? 8 : 2,
            height: BAR_AREA, marginTop: 14,
          }}>
            {visible.map((d) => {
              const goalMet = d.absorbed != null && d.target != null && d.absorbed >= d.target;
              const barH = d.hasData ? Math.max(3, Math.round((d.total / scale) * BAR_AREA)) : 2;
              const verH = d.hasData && d.verified > 0
                ? Math.min(barH, Math.max(2, Math.round((d.verified / scale) * BAR_AREA)))
                : 0;
              return (
                <div key={d.key} style={{ flex: 1, display: "flex", flexDirection: "column",
                  alignItems: "stretch", justifyContent: "flex-end", height: "100%" }}>
                  <div style={{
                    position: "relative",
                    height: barH,
                    borderRadius: view === "week" ? 5 : 2,
                    background: d.hasData
                      ? (goalMet ? BRAND.blue : "rgba(81, 176, 230, 0.45)")
                      : BRAND.card,
                    overflow: "hidden",
                    transition: "height 400ms ease",
                  }}>
                    {verH > 0 && (
                      <div style={{
                        position: "absolute", left: 0, right: 0, bottom: 0,
                        height: verH, background: BRAND.green,
                      }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: view === "week" ? 8 : 2, marginTop: 4 }}>
            {view === "week"
              ? visible.map((d) => (
                  <div key={d.key} className="gw-micro"
                    style={{ flex: 1, textAlign: "center", color: BRAND.gray, fontSize: 10 }}>
                    {dayLetter(d.date)}
                  </div>
                ))
              : (
                <div style={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                  <span className="gw-micro" style={{ color: BRAND.gray, fontSize: 10 }}>
                    {visible.length ? monthLabel(visible[0].date) : ""}
                  </span>
                  <span className="gw-micro" style={{ color: BRAND.gray, fontSize: 10 }}>Today</span>
                </div>
              )}
          </div>
          <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 10 }}>
            {avg != null && <span style={{ color: BRAND.ink, fontWeight: 700 }}>{avg} {UNITS} avg</span>}
            {withTarget.length > 0 && (
              <span> {"\u00B7"} goal met {goalHits} of {withTarget.length} days</span>
            )}
            {withData.length < visible.length && (
              <span> {"\u00B7"} {withData.length} of {visible.length} days on record</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function MyHydration({ onOpenProfile, onMenu }) {
  const { data, demoMode, demoReason, loading, refresh, locked, offline } = useHydrationSummary();
  const [toast, setToast] = useState(null);
  const [showEpiphany, setShowEpiphany] = useState(false);

  const handleIntelAction = (card) => {
    if ((card.id === "demo-card" || card.id === "connect-wearable") && typeof onOpenProfile === "function") {
      onOpenProfile(); return;
    }
    setToast(card.action);
    setTimeout(() => setToast(null), 2200);
  };

  if (locked) {
    // WTR+ upgrade state. Commit 6 replaces this with the full paywall
    // (both price points); until then it is an honest, on-brand ask.
    return (
      <div style={{ background: "#FFFFFF", minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: "0 32px", textAlign: "center",
        fontFamily: "'Montserrat', sans-serif" }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, color: "#51B0E6",
          textTransform: "uppercase", marginBottom: 12 }}>WTR+</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#1A2535", marginBottom: 10 }}>
          Absorption, not just intake.
        </div>
        <div style={{ fontSize: 14, color: "#6B7A8D", lineHeight: 1.6, maxWidth: 320 }}>
          Your Hydration Score, evening projection, and absorption intelligence
          are part of WTR+. Upgrade to unlock them.
        </div>
      </div>
    );
  }

  if (offline) {
    return (
      <div style={{ background: "#FFFFFF", minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: "0 32px", textAlign: "center",
        fontFamily: "'Montserrat', sans-serif" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1A2535", marginBottom: 8 }}>
          Can't reach the server
        </div>
        <div style={{ fontSize: 13, color: "#6B7A8D", marginBottom: 18 }}>
          Your data is safe. Check your connection and try again.
        </div>
        <button onClick={refresh} style={{ background: "#51B0E6", color: "#fff", border: "none",
          borderRadius: 999, padding: "10px 26px", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
          Retry
        </button>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div style={{ background: "#FFFFFF", minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", fontFamily: "'Montserrat', sans-serif", color: "#A6A8AB", fontSize: 14 }}>
        Loading your hydration...
      </div>
    );
  }

  return (
    <div style={{ background: "#FFFFFF", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700&family=Montserrat:wght@400;500;600;700&display=swap');
        .gw-eyebrow { font-family:'Montserrat',sans-serif; font-size:11px; font-weight:700; letter-spacing:0.14em; }
        .gw-score { font-family:'Barlow Condensed',sans-serif; font-size:84px; font-weight:700; line-height:0.95; }
        .gw-status { font-family:'Montserrat',sans-serif; font-size:15px; font-weight:600; }
        .gw-bignum { font-family:'Barlow Condensed',sans-serif; font-size:34px; font-weight:700; color:${BRAND.ink}; }
        .gw-bigunit { font-family:'Montserrat',sans-serif; font-size:16px; font-weight:700; color:${BRAND.ink}; }
        .gw-cardtitle { font-family:'Montserrat',sans-serif; font-size:15px; font-weight:700; color:${BRAND.ink}; }
        .gw-body { font-family:'Montserrat',sans-serif; font-size:13.5px; color:${BRAND.ink}; line-height:1.5; margin:0; }
        .gw-micro { font-family:'Montserrat',sans-serif; font-size:11.5px; line-height:1.45; }
        .gw-dim { font-family:'Montserrat',sans-serif; font-size:13px; color:${BRAND.gray}; }
        .gw-card { background:#FFFFFF; border:1px solid ${BRAND.card}; border-radius:16px; padding:16px; margin-top:14px; }
        .gw-chip { font-family:'Montserrat',sans-serif; font-size:12.5px; font-weight:600; color:${BRAND.blue};
                   background:#FFFFFF; border:1.5px solid ${BRAND.blue}; border-radius:999px; padding:6px 14px; cursor:pointer; white-space:nowrap; }
        .gw-chip:disabled { opacity:0.5; cursor:default; }
        .gw-chip:active { background:${BRAND.blue}; color:#FFFFFF; }
        .gw-action { font-family:'Montserrat',sans-serif; font-size:13px; font-weight:700; color:#FFFFFF;
                     background:${BRAND.blue}; border:none; border-radius:999px; padding:9px 18px; margin-top:12px; cursor:pointer; }
        .gw-rowbtn { display:flex; justify-content:space-between; width:100%; background:none; border:none;
                     padding:0; cursor:pointer; font-family:'Montserrat',sans-serif; font-size:13px; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
      `}</style>

      <AppHeader
        variant="primary"
        title="My Hydration"
        onMenu={onMenu}
      />
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "20px 18px 96px" }}>
        <div style={{ position: "relative", textAlign: "center" }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 22, fontWeight: 700, color: BRAND.ink }}>
            Good day, {data.user.firstName}
          </div>
          {demoMode && (
            <div className="gw-micro" style={{ display: "inline-block", marginTop: 6, padding: "2px 10px",
              border: "1.5px solid " + BRAND.amber, color: BRAND.amber, borderRadius: 999, fontWeight: 700, letterSpacing: "0.08em" }}>
              {demoReason}
            </div>
          )}
        </div>

        <div style={{ marginTop: 18 }}>
          <NhsArc score={data.nhs.score} projected={data.nhs.projectedEveningScore} status={data.nhs.status} />
          <div className="gw-card" style={{ background: BRAND.card, border: "none", textAlign: "center" }}>
            <p className="gw-body" style={{ fontWeight: 600 }}>{data.nhs.projectionNote}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <TierChip tier="developing" />
            </div>
          </div>
        </div>

        <IntakeBar intake={data.intake} />

        {/* CARD ORDER (founder ruling Jul 15 2026, SUPERSEDES the Jul 14
            direction that placed Hydration Efficiency above the target
            card): the target breakdown renders FIRST, then Hydration
            Efficiency. Rationale: "why is my target 153 oz" is the
            question a user asks before "why did verified become
            retained"; bucket size before bucket behavior. */}
        <TargetBreakdown
          rows={data.targetBreakdown}
          targetTotalRef={data.targetTotalRef}
          targetVal={data.intake.target.val}
          targetUnit={data.intake.target.unit}
        />

        {/* Hydration Efficiency (RetentionInsightCard).
            HONESTY GATE unchanged: renders only when the server sends
            data.retention, or in labeled demo mode where the card's
            internal mock is sanctioned under the demo chip. Live users
            never see fabricated retention factors (Commit 5). */}
        {(data.retention || demoMode) && (
          <RetentionInsightCard
            retention={data.retention}
            ci={data.intake.retainedCi}
            onOpenEpiphany={() => setShowEpiphany(true)}
          />
        )}

        <IntakeHistoryCard demoMode={demoMode} />

        <div className="gw-eyebrow" style={{ color: BRAND.gray, marginTop: 22 }}>HYDRATION INSIGHTS</div>
        {data.intelCards.map((c) => (
          <IntelCard key={c.id} card={c} onAction={handleIntelAction} />
        ))}

         <HydrationImpact />

        <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 14, lineHeight: 1.7 }}>
          Powered by Net Hydration Score{"\u2122"}. Absorption, not just intake.
          Absorption efficiency model, not a concentration adjustment model.
          Adaptive range, not a fixed threshold.
        </div>

        <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 10, lineHeight: 1.6, opacity: 0.85 }}>
          Your Hydration Score is a general wellness estimate based on the information
          you provide and your connected devices. It is not a medical measurement or
          medical advice. If you have health concerns, consult a healthcare professional.
        </div>
      </div>

      {showEpiphany && <HydrationEpiphany onClose={() => setShowEpiphany(false)} />}

      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: BRAND.ink, color: "#FFFFFF", borderRadius: 999, padding: "10px 20px",
          fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 600, zIndex: 60 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
